import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

import { expVal } from '@confluence/feature-experiments';

import {
	DEFAULT_ANALYTICS_CARD_PLACEMENT,
	getDialogBesideCardPlacement,
	type AnalyticsCardPlacement,
} from './analyticsDialogCardPlacement.ts';

type DialogRect = Pick<DOMRect, 'left' | 'width' | 'height'>;

type AnalyticsDialogCardPlacementProviderProps = {
	dialogRef: RefObject<HTMLElement | null>;
	children: ReactNode;
};

const AnalyticsDialogCardPlacementContext = createContext<DialogRect | null>(null);

const isSameDialogRect = (left: DialogRect | null, right: DialogRect | null): boolean =>
	left === right ||
	(left != null &&
		right != null &&
		left.left === right.left &&
		left.width === right.width &&
		left.height === right.height);

const isSamePlacement = (left: AnalyticsCardPlacement, right: AnalyticsCardPlacement): boolean =>
	left.position === right.position &&
	left.offset[0] === right.offset[0] &&
	left.offset[1] === right.offset[1];

export const AnalyticsDialogCardPlacementProvider = ({
	dialogRef,
	children,
}: AnalyticsDialogCardPlacementProviderProps): React.JSX.Element => {
	const [dialogRect, setDialogRect] = useState<DialogRect | null>(null);

	useLayoutEffect(() => {
		if (!expVal('ai_agent_analytics_cards', 'isEnabled', false)) {
			return;
		}

		const updateDialogRect = () => {
			const nextRect = dialogRef.current?.getBoundingClientRect() ?? null;
			setDialogRect((currentRect) =>
				isSameDialogRect(currentRect, nextRect) ? currentRect : nextRect,
			);
		};

		updateDialogRect();
		const dialog = dialogRef.current;
		if (typeof ResizeObserver === 'undefined' || !dialog) {
			return;
		}

		const observer = new ResizeObserver(updateDialogRect);
		observer.observe(dialog);
		return () => observer.disconnect();
	}, [dialogRef]);

	return (
		<AnalyticsDialogCardPlacementContext.Provider value={dialogRect}>
			{children}
		</AnalyticsDialogCardPlacementContext.Provider>
	);
};

export const useAnalyticsDialogCardPlacement = (
	triggerRef: RefObject<HTMLElement | null>,
): AnalyticsCardPlacement => {
	const dialogRect = useContext(AnalyticsDialogCardPlacementContext);
	const [placement, setPlacement] = useState<AnalyticsCardPlacement>(
		DEFAULT_ANALYTICS_CARD_PLACEMENT,
	);

	useLayoutEffect(() => {
		if (!expVal('ai_agent_analytics_cards', 'isEnabled', false)) {
			return;
		}

		const nextPlacement = getDialogBesideCardPlacement(
			dialogRect,
			triggerRef.current?.getBoundingClientRect(),
		);
		setPlacement((currentPlacement) =>
			isSamePlacement(currentPlacement, nextPlacement) ? currentPlacement : nextPlacement,
		);
	}, [dialogRect, triggerRef]);

	return placement;
};
